/**
 * ActivationPaymentsCtrl controller
 * Perform payment on activation page
 */

import moment from 'moment'
import { CURRENCY_CONVERTER_TYPES, EXTERNAL_PAYMENT_SYSTEM_TYPES, PAYMENT_SYSTEM_TYPES } from 'app/core/constants'
import { find } from 'app/helpers/object'
import { indexIn, isEnabled } from 'app/helpers/entity'

export const mapFuncForPaymentRequest = (paymentRequest) => {
  if (paymentRequest?.contentSetAddOns) {
    for (const contentSet of paymentRequest.contentSetAddOns) {
      if (['TV_CHANNEL_SET', 'RADIO_CHANNEL_SET'].includes(contentSet.contentType)) {
        contentSet.contentType = 'CHANNEL_SET'
      }
    }
  }

  if (paymentRequest?.paymentSystemType === 'AIRWALLEX') {
    paymentRequest.paymentMethod = 'CREDIT_CARD'
  }

  return paymentRequest
}

/**
 * ActivationPaymentsCtrl controller
 *
 * @class ActivationPaymentsCtrl
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 * @mixes mwApp/common.mixin:m2mCtrlMixin
 *
 * @ngdoc controller
 * @name mwApp/subscribers.controller:ActivationPaymentsCtrl
 */
/* @ngInject */
export default function ActivationPaymentsCtrl ($scope, PaymentRequest, DeviceSlot, Subscriber, alertLiteSvc, authSvc, subscr, extra, scope, CreditCard, $window) {
  var vm = this

  /**
   * Subscriber
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {models.Subscriber}
   */
  vm.subscr = subscr

  /**
   * Extra
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {Object}
   */
  vm.extra = extra

  /**
   * Shared scope
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {Object}
   */
  vm.scope = scope

  /**
   * Payment system types
   *
   * @memberof mwApp/settings.controller:ActivationPaymentsCtrl
   * @name vm.currency_converter_types
   * @type {Object}
   */
  vm.currency_converter_types = CURRENCY_CONVERTER_TYPES

  /**
   * Payment system types
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @name vm.payment_system_types
   * @type {Object}
   */
  vm.payment_system_types = PAYMENT_SYSTEM_TYPES

  /**
   * External payment system types
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @name vm.external_payment_system_types
   * @type {Object}
   */
  vm.external_payment_system_types = EXTERNAL_PAYMENT_SYSTEM_TYPES

  /**
   * Return blank payment request
   * When user changes payment type, for exmple, we need new blank payment request, without old data
   *
   * @function
   */
  const getBlankPaymentRequest = (defaults = {}) => ({
    approvalRequired: false,
    currencyConverterType: extra.currencyConverterTypes[0],
    currency: null,
    // paymentKey: String,
    subscriberId: subscr.id,

    autoPay: false,
    paymentType: null,
    price: null,

    paymentSystem: find(p => p.defaultPaymentSystem)(extra.paymentSystems) || extra.paymentSystems[0],
    externalProvider: null,
    transactionId: null,
    checkNumber: null,
    creditCard: null,
    location: null,
    ...defaults
  })

  /**
   * Blank for PaymentRequest
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {Object}
   */
  let payment = vm.payment = getBlankPaymentRequest()

  /**
   * Returns PymentRequest entity, combined from two, main and child parts of payment
   */
  vm.getPaymentRequest = scope.getPaymentRequest = (forProcessing = true) => {
    const childPayment = scope.getChildPayment(forProcessing)
    if (childPayment) {
      return new PaymentRequest({
        ...payment,
        ...childPayment,
        currencyId: payment.currency ? payment.currency.id : null,
        // paymentKey: String,

        price: payment.price >= 0 ? +payment.price : null,

        creditCardId: payment.creditCard ? payment.creditCard.id : null,
        externalPaymentSystemType: payment.externalProvider ? payment.externalProvider.externalPaymentSystemType : null,
        paymentSystemType: payment.paymentSystem ? payment.paymentSystem.paymentSystemType : null
      })
    }
  }

  vm.showAddonsAutoPay = function () {
    const payment = vm.getPaymentRequest(false)
    if (payment) {
      const currentSubscription = extra.subscriptions.find(s => s.id === payment.subscriptionId)
      const paymentAutoPay = (payment && payment.paymentType === 'Common_Subscription') ? (payment.autoPay || currentSubscription?.periodUnlimited) : vm.subscr.currentPaymentStatement && (vm.subscr.currentPaymentStatement.autoPay || vm.subscr.currentPaymentStatement.periodUnlimited)
      return (payment && ['AUTHORIZE_NET', 'AIRWALLEX'].includes(payment.paymentSystemType)) && paymentAutoPay && payment.contentSetAddOns && payment.contentSetAddOns.reduce((acc, cs) => acc || cs.autoPay, false)
    }
    return false
  }

  vm.getPaymentTrial = function () {
    return authSvc.hasEnabledFlag('FEATURE_TRIAL_SUBSCRIPTION_ENABLED') && !!scope.getChildPayment(false)?.trial
  }

  /*
   * Transform and put settings in scope
   */
  scope.settings = {
    ACTIVATION_CUSTOM_CHARGES:
      Boolean(extra.networkSettings.ACTIVATION_CUSTOM_CHARGES === 'true'),

    ACTIVATION_CUSTOM_SUBSCRIPTIONS:
      Boolean(extra.networkSettings.ACTIVATION_CUSTOM_SUBSCRIPTIONS === 'true'),

    ACTIVATION_DEVICE_LIMIT:
      Number(extra.networkSettings.ACTIVATION_DEVICE_LIMIT),

    ACTIVATION_DEVICE_LINKING:
      Boolean(extra.networkSettings.ACTIVATION_DEVICE_LINKING === 'true'),

    ACTIVATION_DEVICE_SLOTS:
      Boolean(extra.networkSettings.ACTIVATION_DEVICE_SLOTS === 'true'),

    ACTIVATION_LENGTH_LIMIT:
      Number(extra.networkSettings.ACTIVATION_LENGTH_LIMIT),

    PAYMENT_VOID_HOURS:
      Number(extra.networkSettings.PAYMENT_VOID_HOURS)
  }

  /*
   * Get part of payment from child view,
   * Overwritten inside each of child controllers
   */
  scope.getChildPayment = () => {}

  /**
   * Current calculation
   *
   * @type Promise
   */
  let calculateInAction = null

  /*
   * Calculate payment
   */
  scope.calculate = () => {
    vm.unsetDisable()
    scope.isCalculate = true
    const paymentRequest = vm.paymentRequest = vm.getPaymentRequest(false)
    if (paymentRequest && !(vm.extra.trial.allowed && vm.getPaymentTrial())) {
      if (extra.subscriptions.find(s => s.id === paymentRequest.subscriptionId)?.free && paymentRequest.price === 0) {
        paymentRequest.paymentType = 'Free_Subscription'
      }
      vm.calculateInfo = {}
      const promise = calculateInAction =
        paymentRequest._updateUrl()
          .then(() => paymentRequest._calculate(mapFuncForPaymentRequest))
          .then(
            response =>
              promise === calculateInAction
                ? response
                : Promise.reject(new Error('Calculate results rejected')) // reject promise, if it is not current one
          )
          .data()
          .then(
            data => {
              if (!paymentRequest.prorateSubscription && ((paymentRequest.paymentType === 'Free_Subscription' && data.total > 0) || (paymentRequest.paymentType === 'Common_Subscription' && data.total === 0))) {
                vm.calculateInfo = PaymentRequest.cast(data, {
                  transactionFeeAmount: Number,
                  baseCurrency: 'Currency',
                  targetCurrency: 'Currency',
                  convertedTotal: Number,
                  fetchedTime: moment,
                  paymentKey: String,
                  rate: Number,
                  total: Number,
                  subscription: {
                    initialSubTotal: Number,
                    subTotal: Number,
                    ruleApplied: Boolean,
                    prorated: Boolean,
                    name: String
                  },
                  contentAddOns: {
                    initialSubTotal: Number,
                    subTotal: Number,
                    items: []
                  },
                  accessories: {
                    initialSubTotal: Number,
                    subTotal: Number,
                    items: []
                  },
                  devices: { subTotal: Number }
                })

                // set payment price
                payment.price = vm.calculateInfo.baseCurrency
                  ? vm.calculateInfo.total || 0
                  : null

                // set converted price
                vm.convertedPrice = vm.calculateInfo.targetCurrency
                  ? vm.calculateInfo.convertedTotal || 0
                  : null

                vm.contentAddonsAmount = vm.calculateInfo.contentAddOns?.subTotal || 0
                vm.devicesAmount = vm.calculateInfo.devices?.subTotal || 0
                scope.calculate()
              } else {
                vm.calculateInfo = PaymentRequest.cast(data, {
                  transactionFeeAmount: Number,

                  baseCurrency: 'Currency',
                  targetCurrency: 'Currency',
                  convertedTotal: Number,
                  fetchedTime: moment,
                  paymentKey: String,
                  rate: Number,
                  total: Number,
                  subscription: {
                    initialPrice: Number,
                    price: Number,
                    ruleApplied: Boolean,
                    prorated: Boolean,
                    name: String
                  },
                  contentAddOns: {
                    initialSubTotal: Number,
                    subTotal: Number,
                    items: []
                  },
                  accessories: {
                    initialSubTotal: Number,
                    subTotal: Number,
                    items: []
                  },
                  devices: { subTotal: Number }
                })

                // set payment price
                payment.price = vm.calculateInfo.baseCurrency
                  ? vm.calculateInfo.total || 0
                  : null

                // set converted price
                vm.convertedPrice = vm.calculateInfo.targetCurrency
                  ? vm.calculateInfo.convertedTotal || 0
                  : null

                vm.contentAddonsAmount = vm.calculateInfo.contentAddOns?.subTotal || 0
                vm.devicesAmount = vm.calculateInfo.devices?.subTotal || 0

                vm.unsetDisable()
              }
            }
          )
          .catch(() => promise === calculateInAction && vm.setDisable()) // do not disable, if this is not current calculation
    }
  }

  /**
   * Data from calculate response
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {Object}
   */
  vm.calculateInfo = {}

  /**
   * Should we show currency converter and converted total, or no
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   */
  vm.shouldShowConverter = () => {
    const paymentRequest = vm.getPaymentRequest(false)
    if (paymentRequest && !!vm.calculateInfo) {
      const { baseCurrency, targetCurrency, total } = vm.calculateInfo
      return (
        baseCurrency && targetCurrency && baseCurrency !== targetCurrency &&
        total !== 0
        // vm.currencies.length > 1 ||
        // (vm.currencies.length === 1 && !find((p) => p.name === currency.name)(vm.currencies))
      )
    }
    return false
  }

  /**
   * Various validation checks, if we should disable processing or not:
   * - check for credit card existence
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   */
  vm.shouldDisableProcessing = () => {
    const paymentSystemType = vm.payment.paymentSystem.paymentSystemType
    const paymentRequest = vm.getPaymentRequest(false)
    if (paymentRequest && paymentRequest.paymentType === 'Common_Subscription' && extra.subscriptions.find(s => s.id === paymentRequest.subscriptionId)?.free && paymentRequest.price === 0) {
      return false
    }
    if (paymentSystemType === 'TELR' || paymentSystemType === 'AUTHORIZE_NET') {
      if (!vm.cardsByType) {
        return true
      }

      const availableCards = vm.cardsByType.filter(card => card.enabled && card.status === 'OK')
      if (availableCards.length === 0 && !(!vm.subscription?.requirePaymentData && !!paymentRequest?.trial)) {
        return true
      }
    }

    return false
  }

  /**
   * Add slot to subscriber
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   */
  vm.addSlots = function (count) {
    const slots = []
    for (let i = 0; i < count; i++) {
      slots.push(new DeviceSlot({ subscriber: subscr }))
    }
    const promises = []
    slots.forEach(slot => {
      promises.push(slot._save(null, null, null))
    })
    $scope.addedSlotsCount = promises.length
    return Promise.all(promises)
  }

  /**
   * Remove empty slots
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   */
  vm.removeSlots = function (count, afterError = false) {
    const slotForDelete = []
    const sortedSlots = scope.devices.slots.sort((a, b) => {
      if (a.status && !b.status) return -1
      if (!a.status && b.status) return 1
      return 0
    })
    if (sortedSlots.length) {
      for (let i = 0; i < count; i++) {
        slotForDelete.push(sortedSlots[sortedSlots.length - i - 1])
      }
      const promises = []
      slotForDelete.forEach(slot => {
        if (slot) {
          promises.push(slot._delete())
        }
      })
      if (!afterError) {
        $scope.deletedSlotsCount = promises.length
      }
      return Promise.all(promises)
    } else {
      if (count && afterError) {
        const promises = []
        $scope.addedSlots.forEach(slot => {
          if (slot) {
            promises.push(slot._delete(null))
          }
        })
        return Promise.all(promises)
      } else {
        return Promise.resolve()
      }
    }
  }

  /**
   * Remove inactive devices
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   */
  vm.removeInactiveDevices = function (paidDevices) {
    const slotForDelete = []
    const deleteSlot = function (i) {
      if (i > 0) {
        i--
        return slotForDelete[i]._delete().then(() => {
          return deleteSlot(i)
        })
      } else {
        return Promise.resolve()
      }
    }
    if (scope.devices.enabled.length - paidDevices) {
      for (let i = 0; i < scope.devices.enabled.length - paidDevices; i++) {
        slotForDelete.push(scope.devices.enabled.find(slot => slot.sortOrder === scope.devices.enabled.length - i - 1))
      }
      return deleteSlot(slotForDelete.length)
    } else {
      return Promise.resolve()
    }
  }

  vm.atLeastOneBundle = function () {
    return vm.payment.paymentType === 'Content_Addon' && !vm.paymentRequest?.contentSetAddOns.length
  }

  /**
   * Process payment
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   */
  vm.process = () => {
    if (vm.payment.price > 99999.99) {
      alertLiteSvc.alertError('Total can’t be more than 99999,99')
      return
    }

    if (vm.convertedPrice && (vm.convertedPrice > 99999.99)) {
      alertLiteSvc.alertError('Converted total can’t be more than 99999,99')
      return
    }

    if (vm.payment.paymentType === 'Content_Addon' && !vm.paymentRequest?.contentSetAddOns.length) {
      return
    }
    vm.setDisable()
    const paymentRequest = vm.getPaymentRequest()
    const process = function () {
      paymentRequest._updateUrl()
        .then(() => {
          paymentRequest._save(
            request => {
              if (request.paymentSystemType === 'AIRWALLEX') {
                request.paymentMethod = 'CREDIT_CARD'
              }
              if (request.paymentType === 'Add_Device') {
                request.autoPay = vm.subscr.currentPaymentStatement.autoPay
              }
              if (vm.getPaymentTrial()) {
                request.paymentType = 'Trial_Subscription'
              }
              // update `paymentKey` for payment, get it from latest calculation
              if (['Custom_Charge', 'Custom_Subscription'].includes(request.paymentType)) {
                request.paymentKey = null
              } else {
                request.paymentKey = vm.calculateInfo.paymentKey || null
              }
              if (request.paymentType === 'Common_Subscription' && extra.subscriptions.find(s => s.id === request.subscriptionId)?.free && request.price === 0) {
                request.paymentType = 'Free_Subscription'
              }
              if (vm.subscription?.periodUnlimited) {
                request.autoPay = false
              }
              const curPayment = scope.getChildPayment(false)
              if (curPayment && curPayment.$adjustEnabled) {
                request.extraLength = curPayment.$adjustType === 'ADD' ? +curPayment.$extraLength : -1 * (+curPayment.$extraLength)
                if (curPayment.$adjustType === 'SUBTRACT' && curPayment.$adjustMax === 0) {
                  request.extraLength = 0
                }
              }
              if (['Common_Subscription', 'Content_Addon'].includes(request.paymentType) && request.contentSetAddOns) {
                request.contentSetAddOns.forEach(csa => {
                  if (!csa.$adjustEnabled) {
                    if (Object.prototype.hasOwnProperty.call(csa, 'extraLength')) {
                      delete csa.extraLength
                    }
                  }
                  if (Object.prototype.hasOwnProperty.call(csa, '$adjustEnabled')) {
                    delete csa.$adjustEnabled
                  }
                })
              }
              mapFuncForPaymentRequest(request)
            },
            null, 'Payment processed', undefined, vm.showErrorMessage
          ).then(() => {
            Subscriber.load({
              id: subscr.id,
              params: { withPayments: true }
            }).then((res) => {
              subscr = res
              scope.devices.enabled = subscr.devices.filter(isEnabled)
              scope.devices.slots = subscr.deviceSlots
              if (['Common_Subscription', 'Custom_Subscription'].includes(paymentRequest.paymentType) && paymentRequest.override) {
                if ($scope.addSlotsCount < 0) {
                  vm.removeSlots(-1 * $scope.addSlotsCount).then(() => {
                    vm.removeInactiveDevices(paymentRequest.devicesToPay).then(vm.location.reload)
                  })
                } else {
                  vm.removeInactiveDevices(paymentRequest.devicesToPay).then(vm.location.reload)
                }
              } else {
                vm.location.reload()
              }
            })
          })
            .catch(
              e => {
                if (['Common_Subscription', 'Custom_Subscription', 'Free_Subscription'].includes(paymentRequest.paymentType)) {
                  if ($scope.addedSlotsCount > 0) {
                    vm.removeSlots($scope.addedSlotsCount, true).then(() => {
                      $scope.addedSlotsCount = 0
                    })
                  }
                }
                if (e.status === 412) {
                  scope.calculate()
                }
              }
            )
            .finally(vm.unsetDisable)
        })
    }

    if (paymentRequest) {
      if (['Common_Subscription', 'Custom_Subscription'].includes(paymentRequest.paymentType)) {
        $scope.addSlotsCount = paymentRequest.devicesToPay - 1 - scope.devices.enabled.length - scope.devices.slots.length + 1
        if ($scope.addSlotsCount > 0) {
          vm.addSlots($scope.addSlotsCount).then((res) => {
            $scope.addedSlots = res.map(r => new DeviceSlot(r.data))
            process()
          })
        } else {
          process()
        }
      } else {
        process()
      }
    } else {
      vm.unsetDisable()
    }
  }

  vm.showErrorMessage = function (err) {
    if (err.data.includes('Multi set with id:')) {
      const numbers = err.data.match(/\d+/g)
      if (numbers && numbers.length >= 2) {
        const multiSetId = parseInt(numbers[0], 10)
        const contentSetId = parseInt(numbers[1], 10)

        const paymentRequest = vm.getPaymentRequest()

        const multiSet = paymentRequest.contentSetAddOns.find(item => item.id === multiSetId)
        const contentSet = paymentRequest.contentSetAddOns.find(item => item.id === contentSetId)

        const errMessage = `Multi set "${multiSet.name}" already contains content-add-on "${contentSet.name}"`
        return alertLiteSvc.alertError(errMessage)
      }
    }
  }

  /**
   * Currencies
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {models.Currency[]}
   */
  vm.currencies = vm.payment.paymentSystem ? vm.payment.paymentSystem.currencies || [] : []

  /**
   * Payment types (with icons), for drop down select
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {Object}
   */
  vm.payment_types = {
    Common_Subscription: {
      title: 'Subscription Sets',
      icon: 'fa fa-external-link-square'
    },
    Content_Addon: {
      title: 'Bundles',
      icon: 'fa fa-film'
    },
    Accessory: {
      title: 'Accessories',
      icon: 'fa fa-external-link-square'
    },
    Add_Device: {
      title: 'Add Devices / Slots',
      icon: 'fa fa-plus'
    },
    Custom_Subscription: {
      title: 'Custom Subscription',
      icon: 'fa fa-external-link'
    },
    Custom_Charge: {
      title: 'Custom Charge',
      icon: 'fa fa-usd'
    }
  }

  /**
   * Object with boolean functions, to check if user can do actions
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {Object}
   */
  vm.can = scope.can = {
    // small helper to filter dropdown list items by vm.can... methods
    filter: (value) =>
      vm.can[value.key](),

    // Can add something
    Smth: () =>
      Boolean(find(ps => ps.currencies.length)(extra.paymentSystems)) && // has currencies at all
      (
        vm.can.Add_Device() ||
        vm.can.Common_Subscription() ||
        vm.can.Content_Addon() ||
        vm.can.Custom_Charge() ||
        vm.can.Custom_Subscription() ||
        vm.can.Accessory()
      ),

    // Can add devices if subscription has more devices, then paid, and subscriber has inactive devices to add
    Add_Device: () =>
      scope.settings.ACTIVATION_DEVICE_LIMIT > 0 &&
      (
        (
          extra.addDevicesInfoOverride.allowedDevicesToPay > 0 &&
          scope.devices.count() > extra.addDevicesInfoOverride.alreadyPaidDevices
        ) || (
          extra.addDevicesInfoTopUp.allowedDevicesToPay > 0 &&
          scope.devices.count() > extra.addDevicesInfoTopUp.alreadyPaidDevices
        )
      ),

    Content_Addon: () => {
      return vm.extra?.currentSubscription?.hasContentAddons && !extra.currentSubscription.trial
    },

    // Can add subscriptions if there are subscriptions at all, and subscriber has enabled devices
    Common_Subscription: () =>
      scope.settings.ACTIVATION_DEVICE_LIMIT > 0 &&
      extra.subscriptions.length,

    // Can add custom charges
    Custom_Charge: () =>
      scope.settings.ACTIVATION_CUSTOM_CHARGES,

    // Can add custom subscription if subscriber has enabled devices
    Custom_Subscription: () =>
      scope.settings.ACTIVATION_CUSTOM_SUBSCRIPTIONS &&
      scope.settings.ACTIVATION_DEVICE_LIMIT > 0,

    // Can add misc if there are miscs at all
    Accessory: () =>
      extra.currentSubscription && !extra.currentSubscription.trial
  }

  vm.cardsByType = []
  $scope.$watch('vm.scope.cards', () => {
    vm.payment.creditCard = null
    vm.cardsByType = vm.scope.cards.filter((card) => {
      if (vm.payment.paymentSystem.paymentSystemType === 'AUTHORIZE_NET') {
        return card.storageType === vm.payment.paymentSystem.paymentSystemType || card.storageType === 'LOCAL'
      }

      return card.storageType === vm.payment.paymentSystem.paymentSystemType
    })
  }, true)

  /**
   * Actions on change form fields
   *
   * @memberof mwApp/subscribers.controller:ActivationPaymentsCtrl
   * @type {Object}
   */
  vm.change = scope.change = {

    /**
     * Change payment type
     */
    paymentType (paymentType) {
      vm.paymentRequest = {}
      vm.payment = {}
      vm.scope.paymentType = paymentType
      calculateInAction = null // cancel current calculation
      payment = vm.payment = getBlankPaymentRequest({
        paymentType,
        paymentSystem: payment.paymentSystem,
        currency: payment.currency
      })
      vm.currencies = vm.payment.paymentSystem.currencies
      if (indexIn(vm.currencies)(payment.currency) === -1) {
        payment.currency = vm.currencies[0]
      }
      vm.subscription = null
      vm.cardsByType = vm.scope.cards.filter((card) => {
        if (vm.payment.paymentSystem.paymentSystemType === 'AUTHORIZE_NET') {
          return card.storageType === vm.payment.paymentSystem.paymentSystemType || card.storageType === 'LOCAL'
        }

        return card.storageType === vm.payment.paymentSystem.paymentSystemType
      })
    },

    /**
     * Change main subscription (called from Common and Misc, to update AutoPay field visibility)
     */
    mainSubscription (subscription) {
      vm.subscription = subscription
    },

    /**
     * Change payment system
     */
    paymentSystem () {
      vm.currencies = vm.payment.paymentSystem.currencies
      if (indexIn(vm.currencies)(payment.currency) === -1) {
        payment.currency = vm.currencies[0]
      }
      payment.externalProvider = null // reset `externalProvider` field
      payment.autoPay = false // reset `autoPay` field
      payment.creditCard = null // reset `creditCard` field
      scope.calculate()

      vm.cardsByType = vm.scope.cards.filter((card) => {
        if (vm.payment.paymentSystem.paymentSystemType === 'AUTHORIZE_NET') {
          return card.storageType === vm.payment.paymentSystem.paymentSystemType || card.storageType === 'LOCAL'
        }

        return card.storageType === vm.payment.paymentSystem.paymentSystemType
      })
    },

    /**
     * Change external provider
     */
    externalProvider () {
      scope.calculate()
    },

    /**
     * Change currency
     */
    currency () {
      scope.calculate()
    },

    /**
     * Change currency converter
     */
    currencyConverter () {
      scope.calculate()
    }
  }
  $scope.filteredPaymentSystem = function (ps) {
    return !['TELR', 'NEWEBPAY', 'GR4VY_GATEWAY', 'CCPP', 'TRUE_MONEY', 'TRUE_ONLINE', 'TRUE_MOVE_H', 'GOCARDLESS', 'PAYPAL'].includes(ps.value.paymentSystemType)
  }

  vm.contentAddonsCurrency = null
  vm.customSubscriptionDevices = 1

  scope.countCustomSubscriptionDevices = function (devices) {
    vm.customSubscriptionDevices = devices
  }

  scope.countCustomSubscriptionLength = function (length) {
    vm.customSubscriptionLength = length
  }

  $scope.$on('$destroy', function () {
    vm.unsetDisable()
    if (vm.visibilityChangeHandler) {
      $window.removeEventListener('visibilitychange', vm.visibilityChangeHandler)
    }
  })

  vm.visibilityChangeHandler = function () {
    if (!$window.document.hidden && scope.portalRedirectClicked) {
      if (!scope.cards) {
        scope.cards = []
      }
      CreditCard.loadAll({ subscriber: subscr })
        .to(scope.cards)
        .then(() => {
          scope.portalRedirectClicked = false
          if (!$scope.$$phase && !$scope.$root.$$phase) {
            $scope.$apply()
          }
        })
    }
  }

  $window.addEventListener('visibilitychange', vm.visibilityChangeHandler)

  $scope.$watch('vm.payment.paymentSystem', () => {
    const availablePS = vm.extra.paymentSystems.filter((ps) => {
      return $scope.filteredPaymentSystem({ value: ps })
    })
    if (!availablePS.find(ps => ps.id === vm.payment.paymentSystem.id)) {
      vm.payment.paymentSystem = availablePS[0]
    }
  })
}
